import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, WA_DEFAULT, BRAND } from "@/lib/data";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_0_rgba(26,46,42,0.06)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
            <span className="w-10 h-10 rounded-full bg-[#489A88] text-white flex items-center justify-center font-['Outfit'] font-semibold text-sm tracking-tight group-hover:bg-[#2D6D60] transition-colors duration-300">
              FC
            </span>
            <span className="leading-tight">
              <span className="block font-['Outfit'] font-medium text-[#1A2E2A] text-base">Francesca Cicardi</span>
              <span className="block text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Massoterapia · Vanzago</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className="text-sm font-medium text-[#4A5568] hover:text-[#2D6D60] transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#489A88] after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#1A2E2A] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#489A88] transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4" />
              Prenota
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden w-10 h-10 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#1A2E2A]"
              aria-label="Apri menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#FAF8F5]/95 backdrop-blur-2xl flex flex-col"
          >
            <div className="h-20 px-6 flex items-center justify-between">
              <span className="font-['Outfit'] font-medium text-[#1A2E2A]">Francesca Cicardi</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="w-10 h-10 rounded-full border border-[#E2E8F0] flex items-center justify-center text-[#1A2E2A]"
                aria-label="Chiudi menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-['Outfit'] text-4xl font-light text-[#1A2E2A] py-3 border-b border-[#E2E8F0]/70 hover:text-[#489A88] transition-colors"
                >
                  {link.label}
                </motion.a>
              ))}
            </nav>
            <div className="p-8">
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="flex items-center justify-center gap-2 rounded-full bg-[#489A88] text-white font-medium px-6 py-4 w-full"
              >
                <MessageCircle className="w-5 h-5" />
                Prenota su WhatsApp · {BRAND.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
